import { useEffect, useRef } from 'react';

// Make browser back (including edge-swipe) mean "close the topmost popup /
// go back a level" instead of leaving the page.
//
// How: when an overlay opens we push a throwaway history entry. A back gesture
// pops that entry (fires `popstate`) and we run the overlay's close handler
// rather than navigating away. Closing via a button/backdrop instead consumes
// the pushed entry with history.back() so history never accumulates.

const stack = []; // { close, pushed } entries, topmost last
let ignorePops = 0; // pops we triggered ourselves (programmatic close)
let listening = false;

function onPopState() {
  if (ignorePops > 0) {
    ignorePops--;
    if (ignorePops > 0) window.history.back();
    else {
      // A replacement modal must not push into history while an older one is popping.
      for (const entry of stack) {
        if (entry.pushed) continue;
        window.history.pushState({ backGuard: true }, '');
        entry.pushed = true;
      }
    }
    return;
  }
  const entry = stack.pop();
  if (entry && entry.close() === false) {
    // A canceled leave must still intercept the next browser back.
    stack.push(entry);
    window.history.pushState({ backGuard: true }, '');
  }
}

// Push a guard entry: a back gesture pops it and runs onClose instead of
// leaving the page. Returns a disposer that removes the
// guard and consumes its history entry (for programmatic close via button).
export function pushBackGuard(onClose) {
  if (!listening) {
    window.addEventListener('popstate', onPopState);
    listening = true;
  }
  const entry = { close: onClose, pushed: ignorePops === 0 };
  stack.push(entry);
  if (entry.pushed) window.history.pushState({ backGuard: true }, '');

  return () => {
    const i = stack.indexOf(entry);
    if (i === -1) return; // already popped by a back gesture
    stack.splice(i, 1);
    if (entry.pushed && ++ignorePops === 1) window.history.back();
  };
}

export function useBackGuard(isOpen, onClose) {
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;

  useEffect(() => {
    if (!isOpen) return;
    let active = true;
    let dispose;
    // Strict Mode replays mount effects before microtasks; register only the live mount.
    queueMicrotask(() => {
      if (active) dispose = pushBackGuard(() => onCloseRef.current && onCloseRef.current());
    });
    return () => {
      active = false;
      dispose?.();
    };
  }, [isOpen]);
}
